import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Handshake, History } from "lucide-react";
import api from "@/lib/api";
import { money, pct, fdate } from "@/lib/fmt";
import { PageHeader, StatusBadge, VersionBadge, Empty } from "@/components/Common";
import { Autocomplete } from "@/components/Autocomplete";
import { VersionForm } from "@/components/VersionForm";
import { HistoryDialog } from "@/components/HistoryDialog";
import { Button } from "@/components/ui/button";
import { QuoteRowActions } from "@/components/QuoteRowActions";
import { ProbabilityEditor } from "@/components/ProbabilityEditor";

export default function Nego() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [kw, setKw] = useState("");
  const [src, setSrc] = useState(null);
  const [hist, setHist] = useState(null);

  const reload = () => api.get("/quotations", { params: { latest_only: false } }).then((r) => setQuotes(r.data));
  useEffect(() => {
    reload();
    const id = params.get("id");
    if (id) api.get(`/quotations/${id}`).then((r) => { setSrc(r.data); setKw(r.data.display_no); });
  }, [params]);

  const opts = useMemo(() => quotes.filter((q) => q.is_latest && ["open", "expired"].includes(q.display_status)).map((q) => ({ label: q.display_no, sub: `${q.customer_name} • ${q.project_title || "-"} • ${money(q.offer_total, q.currency)}`, data: q })), [quotes]);
  const negos = quotes.filter((q) => q.kind === "nego");

  return (
    <div>
      <PageHeader title="Nego Penawaran" subtitle="Tarik data penawaran, sesuaikan harga hasil negosiasi, lalu convert menjadi penawaran baru." />
      <div className="card-premium p-6 fade-up">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Pilih penawaran yang akan di-nego</p>
        <Autocomplete value={kw} onChange={setKw} options={opts} icon placeholder="Ketik no penawaran atau nama customer..." testId="nego-quote-search" className="max-w-xl" onSelect={(o) => { setKw(o.label); setSrc(o.data); }} />
      </div>
      {src && (
        <div className="mt-6 fade-up" data-testid="nego-workspace">
          <div className="card-premium mb-5 p-5 flex flex-wrap items-center gap-6">
            <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Penawaran</p><p className="font-num font-semibold">{src.display_no}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Customer</p><p className="font-medium">{src.customer_name}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Total Saat Ini</p><p className="font-num font-semibold">{money(src.offer_total, src.currency)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Margin</p><p className="font-num font-semibold">{pct(src.final_margin)}</p></div>
            <VersionBadge q={src} />
            <Button size="sm" variant="ghost" onClick={() => setHist(src.id)} data-testid="nego-source-history-btn"><History className="h-4 w-4 mr-1" />Riwayat</Button>
            <ProbabilityEditor quote={src} onSaved={(p) => setSrc({ ...src, probability: p })} testId="nego-prob" />
          </div>
          <VersionForm source={src} mode="nego" onSaved={(q) => nav(`/quotations/${q.id}`)} />
        </div>
      )}
      <div className="mt-8 card-premium fade-up">
        <h3 className="font-heading p-5 text-lg font-semibold flex items-center gap-2"><Handshake className="h-5 w-5 text-amber-600" />Penawaran Hasil Nego</h3>
        <div className="overflow-x-auto">
          <table className="tbl w-full" data-testid="nego-list-table">
            <thead><tr><th>No</th><th>Tanggal</th><th>Customer</th><th>Sales</th><th className="text-right">Total</th><th className="text-right">Margin</th><th>Remark Nego</th><th>Versi</th><th>Status</th><th /></tr></thead>
            <tbody>
              {negos.map((q, i) => (
                <tr key={q.id} className="cursor-pointer" onClick={() => nav(`/quotations/${q.id}`)} data-testid={`nego-row-${i}`}>
                  <td className="font-num text-sm font-semibold text-blue-800">{q.display_no}</td>
                  <td className="text-sm">{fdate(q.created_at)}</td>
                  <td>{q.customer_name}</td>
                  <td className="text-sm">{q.sales_name}</td>
                  <td className="text-right font-num">{money(q.offer_total, q.currency)}</td>
                  <td className="text-right font-num">{pct(q.final_margin)}</td>
                  <td className="text-xs text-amber-800 max-w-[260px]">{q.nego_remark}</td>
                  <td><VersionBadge q={q} /></td>
                  <td><StatusBadge status={q.display_status} /></td>
                  <td className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}><Button size="icon" variant="ghost" onClick={() => setHist(q.id)} data-testid={`nego-history-${i}`}><History className="h-4 w-4" /></Button><QuoteRowActions id={q.id} displayNo={q.display_no} fileName={q.pdf_filename} versionOnly onDeleted={reload} testPrefix={`nego-${i}`} locked={["deal", "lost"].includes(q.status)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!negos.length && <Empty text="Belum ada penawaran hasil nego" />}
        </div>
      </div>
      <HistoryDialog quoteId={hist} open={!!hist} onOpenChange={(o) => !o && setHist(null)} />
    </div>
  );
}
